import React, { useState, useEffect, useCallback } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Row, Col, Button, Alert, ListGroup, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { LinkContainer } from "react-router-bootstrap";
import Select from "react-select";
import Moment from "moment";
import { GenericTable } from "components/GenericTable";
import { _s3GetURL } from "lib/awsLib";

function EditablePropertyKeyCell({ getValue, rowIndex, onBlur }) {
    const [localValue, setLocalValue] = useState(getValue() || "");
    useEffect(() => { setLocalValue(getValue() || ""); }, [getValue()]);
    return (
        <input
            type="text"
            className="no-border form-control"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value.toUpperCase())}
            onBlur={() => onBlur(rowIndex, localValue)}
        />
    );
}

function PropertyArticleList({
    data,
    isAuthenticated,
    controlType,
    saveRecord,
    deleteRecord,
    updateTableState,
    validateRelatedRecord,
    articleCategories,
    propertyid,
    propertykey,
    idList,
    articleid,
    user,
}) {
    const [tableData, setTableData] = useState(data || []);
    const [selectedArticleCategory, setSelectedArticleCategory] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const dataSet = "propertyArticles";

    // Sync data from props and generate S3 URLs for files
    useEffect(() => {
        const loadUrls = async () => {
            if (data && data.length > 0) {
                const dataWithUrls = await Promise.all(
                    data.map(async (item) => {
                        if (item.file_name && item.file_type && !item.url) {
                            try {
                                const file = `${item.file_name}.${item.file_type}`;
                                const url = await _s3GetURL(file, 'public/article');
                                return { ...item, url };
                            } catch (error) {
                                console.error(`Failed to generate URL for ${item.file_name}:`, error);
                                return item;
                            }
                        }
                        return item;
                    })
                );
                setTableData(dataWithUrls);
            } else {
                setTableData(data || []);
            }
        };
        loadUrls();
    }, [data]);

    // Column setup
    const columnHelper = createColumnHelper();

    const handleDeleteRecord = useCallback(async (row) => {
        if (window.confirm("Are you sure you want to delete this article link?")) {
            try {
                const record = { ...row.original, archived_date: new Date() };
                await saveRecord(record, dataSet, "article_property_link_id");
                const newData = tableData.filter((_, i) => i !== row.index);
                setTableData(newData);
                updateTableState(newData, dataSet);
            } catch (error) {
                console.error("Delete error:", error);
                setErrorMessage("Failed to delete record");
                setShowAlert(true);
            }
        }
    }, [saveRecord, tableData, updateTableState, dataSet]);

    const handleFilterChange = useCallback((selectedOption) => {
        if (selectedOption) {
            const filtered = data.filter((item) =>
                item.article_category_code === selectedOption.value
            );
            setTableData(filtered);
        } else {
            setTableData(data || []);
        }
        setSelectedArticleCategory(selectedOption);
    }, [data]);

    const tableMeta = {
        updateData: (rowIndex, columnId, value) => {
            const nextRows = [...tableData];
            nextRows[rowIndex] = { ...nextRows[rowIndex], [columnId]: value };
            updateTableState(nextRows, dataSet);
        },
        handleSave: async ({ row }) => {
            const record = row.original;
            try {
                const result = await saveRecord(record, dataSet, "article_property_link_id");
                if (result && result.result === false) {
                    setErrorMessage(result.error_message || "Save failed");
                    setShowAlert(true);
                }
            } catch (error) {
                setErrorMessage("Failed to save record");
                setShowAlert(true);
            }
        },
        handleDelete: ({ row }) => {
            handleDeleteRecord(row);
        },
    };

    // Full columns for main table view
    const fullColumns = [
        columnHelper.accessor("article_property_link_id", {
            id: "article_property_link_id",
            header: "ID",
            size: 60,
        }),
        columnHelper.accessor("article_id", {
            id: "article_id",
            header: "Article ID",
            size: 60,
            cell: (info) => {
                const value = info.getValue();
                return value ? (
                    <Link
                        to={{
                            pathname: `/articles/${value}`,
                            state: { propertyidlist: idList },
                        }}
                    >
                        {value}
                    </Link>
                ) : "";
            },
        }),
        columnHelper.accessor("source", {
            id: "source",
            header: "Source",
            size: 200,
        }),
        columnHelper.accessor("brief_description", {
            id: "brief_description",
            header: "Description",
            size: 400,
        }),
        columnHelper.accessor("category", {
            id: "category",
            header: "Article Category",
            size: 200,
        }),
        columnHelper.accessor("article_date", {
            id: "article_date",
            header: "Article Date",
            cell: (info) => {
                const date = info.getValue();
                return date ? Moment(date).local().format("DD-MM-YYYY") : "";
            },
            size: 105,
        }),
        columnHelper.accessor("file_name", {
            id: "file_name",
            header: "File Name",
            cell: (info) => {
                const filename = info.getValue();
                const filetype = info.row.original.file_type;
                const url = info.row.original.url;
                return url && filename ? (
                    <a target="_blank" rel="noopener noreferrer" href={url}>
                        {filename}.{filetype}
                    </a>
                ) : (
                    <span>{filename}{filetype ? `.${filetype}` : ""}</span>
                );
            },
            size: 400,
        }),
        columnHelper.accessor("file_type", {
            id: "file_type",
            header: "File Type",
            size: 105,
        }),
        columnHelper.display({
            id: "actions",
            header: "",
            cell: (info) => (
                <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={() => handleDeleteRecord(info.row)}
                >
                    Delete
                </Button>
            ),
            size: 65,
        }),
    ];

    const handlePropertyKeyBlur = useCallback(async (rowIndex, value) => {
        try {
            const record = { ...tableData[rowIndex], property_key: value };
            const validation = validateRelatedRecord(record, dataSet);
            if (!validation || validation[0] !== true) {
                setErrorMessage(validation ? validation[1] : "Validation error");
                setShowAlert(true);
                return;
            }
            setShowAlert(false);
            // Update state then save
            const newData = tableData.map((row, i) =>
                i === rowIndex ? { ...row, property_key: value, modified: true } : row
            );
            setTableData(newData);
            updateTableState(newData, dataSet);
            const result = await saveRecord(record, dataSet, "article_property_link_id");
            if (result && result.result === false) {
                setErrorMessage(result.error_message);
                setShowAlert(true);
            } else if (result && result.id) {
                const updatedData = newData.map((row, i) =>
                    i === rowIndex ? { ...row, article_property_link_id: result.id, new_record: false } : row
                );
                setTableData(updatedData);
                updateTableState(updatedData, dataSet);
            }
        } catch (error) {
            console.error("handlePropertyKeyBlur error:", error);
            setErrorMessage("Error: " + (error.message || String(error)));
            setShowAlert(true);
        }
    }, [tableData, validateRelatedRecord, saveRecord, updateTableState, dataSet]);

    // Simplified columns for modal (table2) view
    const modalColumns = [
        columnHelper.accessor("article_property_link_id", {
            id: "article_property_link_id",
            header: "ID",
            size: 80,
        }),
        columnHelper.accessor("article_id", {
            id: "article_id",
            header: "Article ID",
            size: 150,
        }),
        columnHelper.accessor("property_key", {
            id: "property_key",
            header: "Cityscope Reference",
            size: 300,
            cell: (info) => (
                <EditablePropertyKeyCell
                    getValue={info.getValue}
                    rowIndex={info.row.index}
                    onBlur={handlePropertyKeyBlur}
                />
            ),
        }),
        columnHelper.display({
            id: "actions",
            header: "",
            cell: (info) => (
                <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={() => handleDeleteRecord(info.row)}
                >
                    Delete
                </Button>
            ),
            size: 80,
        }),
    ];

    const columns = controlType === "table2" ? modalColumns : fullColumns;

    const categoryOptions =
        articleCategories && Array.isArray(articleCategories)
            ? articleCategories.map((cat) => ({
                  value: cat.article_category_code,
                  label: cat.description || cat.article_category_code,
              }))
            : [];

    if (!isAuthenticated) {
        return <div>Not authenticated</div>;
    }

    return (
        <div className="PropertyArticleList">
            <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Uh Oh!</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">{errorMessage}</Alert>
                </Modal.Body>
            </Modal>

            {controlType === "table" && (
                <>
                    <Row className="mb-3">
                        <Col>
                            <Select
                                isClearable
                                options={categoryOptions}
                                value={selectedArticleCategory}
                                onChange={handleFilterChange}
                                placeholder="Filter by Article Category..."
                            />
                        </Col>
                        <Col className="text-end">
                            <LinkContainer
                                to={{
                                    pathname: `/properties/${propertykey}/articles/new`,
                                    search: `?propertyid=${propertyid}`,
                                }}
                            >
                                <Button variant="secondary" size="sm">
                                    Add
                                </Button>
                            </LinkContainer>
                        </Col>
                    </Row>

                    <Row className="show-Container">
                        <Col sm={12} md={12} lg={12}>
                            <GenericTable
                                columns={columns}
                                data={tableData}
                                pageSize={10}
                                enablePagination={true}
                                hasHeaderSorting={true}
                                meta={tableMeta}
                            />
                        </Col>
                    </Row>
                </>
            )}

            {controlType === "list" && (
                <ListGroup>
                    {tableData.map((d, i) => (
                        <LinkContainer
                            key={i}
                            to={{
                                pathname: `/properties/${d.property_key}`,
                                state: { tab: "articles", data: (idList || []).indexOf(d.property_key) >= 0 ? idList : [] },
                            }}
                        >
                            <ListGroup.Item as="a">
                                {d.property_key}
                            </ListGroup.Item>
                        </LinkContainer>
                    ))}
                </ListGroup>
            )}

            {controlType === "table2" && (
                <>
                    <Row className="mb-3">
                        <Col className="text-end">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    const newRecord = {
                                        property_id: null,
                                        property_key: "",
                                        article_id: articleid,
                                        article_date: null,
                                        source: "",
                                        brief_description: "",
                                        article_category_code: "",
                                        file_name: "",
                                        file_type: null,
                                        created_by: user,
                                        last_edited_by: user,
                                        new_record: true,
                                    };
                                    const newData = [newRecord, ...tableData];
                                    setTableData(newData);
                                    updateTableState(newData, "propertyArticles");
                                }}
                            >
                                Add
                            </Button>
                        </Col>
                    </Row>

                    <Row className="show-Container">
                        <Col sm={12} md={12} lg={12}>
                            <GenericTable
                                columns={columns}
                                data={tableData}
                                pageSize={10}
                                enablePagination={true}
                                hasHeaderSorting={true}
                                meta={tableMeta}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
}

export default PropertyArticleList;
